<template>
    <div class="task-complete-rule">
        <label>{{ title }}</label>
        <DsMultiselect
            v-model="taskSelected"
            class="rule-task-selected"
            data-qa="rule-task-selected"
            :placeholder="$t('funnel.campaign.task.complete.task')"
            bind-value-only
            :options="filteredTaskOptions"
            @input="taskSelectedChange"
        />
        <label class="separator">{{ $t('funnel.campaign.task.complete.lower.is') }}</label>
        <DsMultiselect
            v-if="!completedByDefault"
            v-model="taskOutcomesSelected"
            :readonly="!outcomeOptionsAvailable"
            class="rule-task-outcome-selected"
            data-qa="rule-task-outcome-selected"
            :placeholder="$t('funnel.campaign.task.complete.outcome')"
            bind-value-only
            multiple
            searchable
            :options="taskOutcomeOptions"
            @input="outcomeSelectedChange"
        >
            <template #selection="{ option, remove }">
                <label class="or-rule-separator">{{ $t('funnel.campaign.task.complete.lower.or') }}</label>
                <DsChip
                    remove
                    with-interaction
                    @mousedown.native.stop.prevent="remove"
                >
                    <template #default>
                        {{ option.chipLabel || option.label }}
                    </template>
                </DsChip>
            </template>
        </DsMultiselect>
        <DsMultiselect
            v-if="completedByDefault"
            :value="completedByDefaultValue"
            class="rule-task-outcome-selected rule-task-outcome-selected-default"
            data-qa="rule-task-outcome-selected-default"
            :placeholder="$t('funnel.campaign.task.complete.outcome')"
            bind-value-only
            multiple
            searchable
            :options="$options.DEFAULT_OUTCOME_OPTIONS"
            @input="setIsCompletedByDefault"
        />
        <DsIconButton
            name="trash-2"
            class="rule-task-delete"
            data-qa="rule-task-delete"
            @click="deleteRule"
        />
    </div>
</template>

<script>
import {
    getOutcomesByIds,
} from '../../../configuration/task.flow.api';

export const DEFAULT_OUTCOME_OPTIONS = [{
    value: 1,
    label: 'Is completed (default)',
}, {
    value: null,
    label: 'You don\'t have any outcomes associated with this task',
}];

export default {
    DEFAULT_OUTCOME_OPTIONS,
    props: {
        id: {
            type: Number,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        taskOptions: {
            type: Array,
            required: true,
        },
        unavailableTaskIds: {
            type: Array,
            required: true,
        },
        availableTasksAndOutcomes: {
            type: Array,
            required: true,
        },
        configuration: {
            type: Object,
            required: true,
        },
    },

    emits: ['task-selection', 'update-configuration', 'delete-rule', 'configuration-warning'],

    data() {
        return {
            taskSelected: '',
            taskOutcomesSelected: [],
            taskOutcomeOptions: [],
            completedByDefault: false,
            completedByDefaultValue: [],
        };
    },

    computed: {
        outcomeOptionsAvailable() {
            return this.taskOutcomeOptions?.length > 0;
        },

        filteredTaskOptions() {
            return this.taskOptions.filter((option) => !this.tasksToExclude.includes(option.value))
                .map((option) => ({
                    value: `${option.value}`,
                    label: option.label,
                }));
        },

        tasksToExclude() {
            return this.unavailableTaskIds.filter((element) => element !== this.taskSelected);
        },
    },

    watch: {
        taskSelected(value, oldValue) {
            if (value !== oldValue) {
                this.$emit('task-selection', { value, oldValue });
            }
        },
    },

    mounted() {
        this.loadConfig();
    },

    methods: {
        async taskSelectedChange() {
            this.clearSelectedTaskOutcomes();

            await this.getTaskOutcomes();
            this.$emit('update-configuration', { rulePosition: this.id, taskId: this.taskSelected, outcomeIds: [] });
        },

        async getTaskOutcomes() {
            const availableOutcomeIds = this.availableTasksAndOutcomes
                .filter((element) => element.id === this.taskSelected)
                .map((element) => (
                    element.outcomeIds?.toString()
                ));

            if (availableOutcomeIds?.length > 0 && availableOutcomeIds[0] !== '') {
                this.taskOutcomeOptions = await getOutcomesByIds(availableOutcomeIds[0]);
            } else if (this.taskSelected && this.taskSelected !== '') {
                this.setIsCompletedByDefault();
            }
        },

        outcomeSelectedChange() {
            this.$emit('update-configuration', { rulePosition: this.id, taskId: this.taskSelected, outcomeIds: this.taskOutcomesSelected });
        },

        clearSelectedTaskOutcomes() {
            this.taskOutcomeOptions = [];
            this.taskOutcomesSelected = [];
            this.completedByDefaultValue = [];
            this.completedByDefault = false;
        },

        deleteRule() {
            this.clearSelectedTaskOutcomes();
            this.$emit('delete-rule', { rulePosition: this.id, taskId: this.taskSelected });
            this.taskSelected = '';
        },

        async loadConfig() {
            if (this.configuration?.taskId !== '') {
                this.taskSelected = this.configuration.taskId;

                await this.getTaskOutcomes();
                this.validatePreviousConfig(this.configuration.taskOutcomeIds);
            }
        },

        validatePreviousConfig(previousConfiguration) {
            this.taskOutcomesSelected = this.configuration.taskOutcomeIds;

            const outcomesSet = new Set(this.taskOutcomeOptions.map((item) => item.value));

            if (previousConfiguration && !previousConfiguration.every((item) => outcomesSet.has(item))) {
                this.taskOutcomesSelected = this.taskOutcomesSelected.filter((id) => this.taskOutcomeOptions.some((item) => item.value === id));

                this.$emit('configuration-warning', { rulePosition: this.id, taskId: this.taskSelected, outcomeIds: this.taskOutcomesSelected });
            }
        },

        setIsCompletedByDefault() {
            this.completedByDefault = true;
            this.completedByDefaultValue = [1];
        },
    },
};
</script>
<style lang="scss" scoped>
div.task-complete-rule {
    display: grid;
    grid-template-areas: 'rulelabel rulelabel rulelabel rulelabel' 'ruletaskselected rulelabelseparator ruletaskoutcomeselected ruletaskdelete';
    grid-template-columns: 45% auto 45% auto;
    align-items: center;
    padding: $spacing-125 px-to-rem(38) $spacing-200;
    border-top: solid 1px $color-gray-400;
    margin-left: 0;
    margin-right: 0;
}
div.task-complete-rule label {
    grid-area: rulelabel;
    font-weight: 500;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    color: $color-ink-900;
    padding-bottom: $spacing-050;
}
div.task-complete-rule label.separator {
    grid-area: rulelabelseparator;
    font-weight: 500;
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    color: $color-ink-900;
    margin: auto;
    padding: 0;
}
div.task-complete-rule .rule-task-selected {
    grid-area: ruletaskselected;
    height: fit-content;
    margin: auto;
    padding: 0;
}
div.task-complete-rule .rule-task-outcome-selected {
    grid-area: ruletaskoutcomeselected;
    height: fit-content;
    margin: auto;
    padding: 0;
}
div.task-complete-rule .rule-task-delete {
    grid-area: ruletaskdelete;
    height: fit-content;
    margin: auto;
    padding: 0;
}
label.or-rule-separator:first-of-type {
    display: none;
}
label.or-rule-separator:not(:first-of-type) {
    font-size: $font-size-xs;
    color: $color-text-subtle;
    line-height: $font-size-md;
}
</style>
<style lang="scss">
section.completion-rule div.rule-task-outcome-selected-default.multiselect.searchable.multiple div.dropdown-menu div.menu ul  li {
    font-size: $font-size-md;
    font-weight: 400;
    line-height: $line-height-lg;
    letter-spacing: 0;
    text-align: center;
    color: $color-text-subtle;
}
section.completion-rule div.rule-task-outcome-selected-default.multiselect.searchable.multiple div.dropdown-menu div.menu ul  li div.menu-item {
    padding: $spacing-400 $spacing-125;
}
</style>
